import React, { useCallback, useMemo, useRef, useState } from 'react';
import { gql } from '@apollo/react-hooks';
import DataGrid, { defaultActions, Filter } from '#/common/components/DataGrid';
import { PageAction, PageActions } from '#/common/components/SubPage';
import type { Action, AssetPlanActiveScenarioItem } from '#/graphql/types';
import { getAssetPlanScenarioItemGroupList, getAssetPlanScenarioItemList } from '#/graphql/queries';
import { assetPlanActionItems } from '../../utils/constants';
import useToast from '#/common/hooks/useToast';
import useSavePlanItemsMutation from '#/modules/asset-plan/hooks/useSavePlanItemsMutation';
import buildColumns from './columns';
import { RemoteOperations, Grouping, GroupItem, Summary } from 'devextreme-react/data-grid';

const getItemListQuery = gql(getAssetPlanScenarioItemList);
const getGroupItemGroupListQuery = gql(getAssetPlanScenarioItemGroupList);

interface AssetPlanItemsDataGridProps {
  activeScenarioId: string;
  startYear?: number;
  endYear?: number;
}
type Overrides = Record<
  string,
  { assetId: string; action?: Action; interventionYear?: number | null }
>;

const isAssetItem = (itemType: string) => itemType.toLowerCase() === 'asset';

const isFindingItem = (itemType: string) => itemType.toLowerCase() === 'finding';

const isCapitalNeedItem = (itemType: string) => itemType.toLowerCase() === 'capital_need';

const AssetPlanItemsDataGrid: React.FC<AssetPlanItemsDataGridProps> = ({
  activeScenarioId,
  startYear,
  endYear,
}) => {
  const { alert } = useToast();
  const permanentFilters: Filter[] = useMemo(
    () => [['assetPlanScenarioId', '=', activeScenarioId]],
    [activeScenarioId],
  );
  const dgRef = useRef<any>(null);
  const [overrides, setOverrides] = useState<Overrides>({});
  const [isSaving, setIsSaving] = useState(false);
  const { saveItems } = useSavePlanItemsMutation();

  const toAllowedActionItems = useCallback(
    (items: typeof assetPlanActionItems) =>
      items.map((item) => ({
        ...item,
        key: item.key as Action,
      })),
    [],
  );

  const getAllowedActionItems = useCallback(
    (row: AssetPlanActiveScenarioItem) => {
      if (isAssetItem(row.itemType)) {
        return toAllowedActionItems(
          assetPlanActionItems.filter((item) => {
            if (item.key === 'replace') return true;
            if (item.key === 'repair') return row.hasRelatedItems;
            return false;
          }),
        );
      }

      if (isFindingItem(row.itemType)) {
        return toAllowedActionItems(
          assetPlanActionItems.filter(
            (item) => item.key === 'repair' || item.key === 'defer' || item.key === 'ignore',
          ),
        );
      }

      if (isCapitalNeedItem(row.itemType)) {
        return toAllowedActionItems(
          assetPlanActionItems.filter(
            (item) => item.key === 'address' || item.key === 'defer' || item.key === 'ignore',
          ),
        );
      }

      return toAllowedActionItems(assetPlanActionItems);
    },
    [toAllowedActionItems],
  );

  const hasPendingChanges = Object.keys(overrides).length > 0;

  const interventionYearOptions = useMemo(() => {
    if (
      startYear === undefined ||
      endYear === undefined ||
      !Number.isInteger(startYear) ||
      !Number.isInteger(endYear) ||
      endYear < startYear
    ) {
      return [] as number[];
    }

    return Array.from({ length: endYear - startYear + 1 }, (_, index) => startYear + index);
  }, [startYear, endYear]);

  const saveChanges = useCallback(async () => {
    const ids = Object.keys(overrides);
    if (!ids.length) return;

    const items = ids.map((id) => ({
      id,
      assetId: overrides[id].assetId,
      action: overrides[id].action,
      interventionYear: overrides[id].interventionYear,
    }));

    try {
      const response = await saveItems({
        assetPlanScenarioId: activeScenarioId,
        items,
      });

      const errors = response?.errors?.filter((err): err is string => !!err);
      if (errors?.length) {
        void alert('error', errors.join(';\n'));
        return;
      }

      const updatedCount = response?.data?.updatedCount ?? 0;

      // Keep local overrides applied until the grid has reloaded to avoid a stale-value flash.
      const refreshPromise = dgRef.current?.instance.refresh();
      if (refreshPromise && typeof refreshPromise.then === 'function') await refreshPromise;

      setOverrides({});

      void alert('success', `${updatedCount} scenario item changes saved.`);
    } catch (error) {
      console.error(error);
      void alert('error', 'Failed to save scenario item changes.');
    }
  }, [overrides, activeScenarioId, alert, saveItems]);

  const overridesRef = useRef(overrides);
  overridesRef.current = overrides;

  const columns = useMemo(() => {
    const onActionChange = (rowId: string, value: Action, row: AssetPlanActiveScenarioItem) => {
      setOverrides((prev) => ({
        ...prev,
        [rowId]: { ...prev[rowId], assetId: row.assetId, action: value },
      }));
    };

    const getActionValue = (rowId: string, fallbackValue?: Action) =>
      overridesRef.current[rowId]?.action ?? fallbackValue;

    const onInterventionYearChange = (
      rowId: string,
      value: number | null,
      row: AssetPlanActiveScenarioItem,
    ) => {
      setOverrides((prev) => ({
        ...prev,
        [rowId]: { ...prev[rowId], assetId: row.assetId, interventionYear: value },
      }));
    };

    const getInterventionYearValue = (rowId: string, fallbackValue?: number | null) =>
      overridesRef.current[rowId]?.interventionYear ?? fallbackValue ?? null;

    const isActionOverridden = (rowId: string) => overridesRef.current[rowId]?.action !== undefined;

    const isInterventionYearOverridden = (rowId: string) =>
      overridesRef.current[rowId]?.interventionYear !== undefined;

    return buildColumns({
      interventionYearOptions,
      getAllowedActionItems,
      onActionChange,
      getActionValue,
      onInterventionYearChange,
      getInterventionYearValue,
      isActionOverridden,
      isInterventionYearOverridden,
    });
  }, [interventionYearOptions, getAllowedActionItems]);

  const topActions = useMemo<PageAction[]>(
    () => [
      {
        id: 'clear-overrides',
        alwaysShow: true,
        type: 'button',
        props: {
          icon: 'xmark',
          label: 'Clear Changes',
          responsive: true,
          disabled: !hasPendingChanges || isSaving,
          onClick: () => setOverrides({}),
        },
      },
      {
        id: 'save-asset-plan-scenario-items-top',
        alwaysShow: true,
        type: 'button',
        props: {
          icon: 'floppy-disk',
          label: 'Save',
          responsive: true,
          type: 'submit',
          disabled: !hasPendingChanges || isSaving,
          onClick: async () => {
            dgRef.current?.instance.beginCustomLoading('Saving scenario changes...');
            setIsSaving(true);
            try {
              await saveChanges();
            } finally {
              setIsSaving(false);
              dgRef.current?.instance.endCustomLoading();
            }
          },
        },
      },
    ],
    [hasPendingChanges, isSaving, saveChanges],
  );

  return (
    <>
      <PageActions actions={topActions} />
      <DataGrid<AssetPlanActiveScenarioItem>
        id="asset-plan-items"
        pluralText="Plan Items"
        singularText="Plan Item"
        columns={columns}
        getItemsQuery={getItemListQuery}
        getGroupedItemList={getGroupItemGroupListQuery}
        permanentFilters={permanentFilters}
        selectionMode="none"
        setRef={(ref) => {
          dgRef.current = ref;
        }}
        actions={defaultActions}>
        <RemoteOperations groupPaging paging filtering sorting />
        <Grouping autoExpandAll={true} />
        <Summary>
          <GroupItem column="itemType" showInGroupFooter={false} alignByColumn={true} />
          <GroupItem column="interventionCost" showInGroupFooter={false} alignByColumn={true} />
          <GroupItem column="assetReplacementCost" showInGroupFooter={false} alignByColumn={true} />
          <GroupItem column="action" showInGroupFooter={false} alignByColumn={true} />
          <GroupItem column="interventionYear" showInGroupFooter={false} alignByColumn={true} />
          <GroupItem column="score" showInGroupFooter={false} alignByColumn={true} />
          <GroupItem column="priority" showInGroupFooter={false} alignByColumn={true} />
        </Summary>
      </DataGrid>
    </>
  );
};

export default React.memo(AssetPlanItemsDataGrid);
